<script setup>
import notification from "@/utils/plugins/NotificationService";
import { computed, h, onMounted, ref, shallowRef } from "vue";


import MeetingRoomService from "@/api/services/meetingRoom.service";
import IconMeetingRoomReservation from "@/components/icons/IconMeetingRoomReservation.vue";

// eslint-disable-next-line no-undef
const emit = defineEmits(["close", "finish"]);
// eslint-disable-next-line no-undef
const props = defineProps({
    meetingRoom: {
        type: Object,
        default: null,
    },
    initialStartAt: {
        type: String,
        default: null,
    },
    publicHolidayDates: {
        type: Array,
        default: () => [],
    },
    readonly: {
        type: Boolean,
        default: false,
    },
});

const startDay = ref("");
const startTime = ref("");
const endDay = ref("");
const endTime = ref("");
const startDate = ref(null);
const endDate = ref(null);
const subject = ref("");
const today = ref(new Date());

const errors = ref({
    startDate: false,
    endDate: false,
    subject: false,
});

const isEditing = computed(() => {
    return !!props.meetingRoom?.id;
});

const isReadonly = computed(() => {
    return props.readonly;
});

const dialogTitle = computed(() => {
    if (isReadonly.value) {
        return "Ver reserva";
    }

    return isEditing.value ? "Editar reserva" : "Reservar sala";
});

const customPrefix = shallowRef({
    render() {
        return h("img", {
            src: "/calendar-blue.svg",
            style: "width: 16px; height: 16px; margin-right: 10px;",
        });
    },
});

const startMinTime = computed(() => {
    if (!startDay.value) {
        return "00:00";
    }

    const todayDate = formatDatePart(new Date());
    if (startDay.value !== todayDate) {
        return "00:00";
    }

    const nextSlot = getNextStrictHalfHour(new Date());
    const nextSlotTime = formatTimePart(nextSlot);

    if (isEditing.value && !isReadonly.value && startTime.value && startTime.value < nextSlotTime) {
        return startTime.value;
    }

    return nextSlotTime;
});

const endMinTime = computed(() => {
    if (!startDay.value || !endDay.value || startDay.value !== endDay.value || !startTime.value) {
        return "00:00";
    }

    const nextSlot = addMinutesToTime(startTime.value, 30);
    return nextSlot ?? "23:30";
});

onMounted(() => {
    if (isEditing.value) {
        loadReservationData();
        return;
    }

    if (props.initialStartAt) {
        loadInitialReservationData();
    } else {
        const roundedStart = getNextStrictHalfHour(new Date());
        syncStartPartsFromDate(roundedStart);

        const defaultEnd = new Date(roundedStart);
        defaultEnd.setMinutes(defaultEnd.getMinutes() + 30);
        syncEndPartsFromDate(defaultEnd);
    }

    const newToday = new Date();
    newToday.setHours(0, 0, 0, 0);
    today.value = newToday;
});

const closeDialog = () => {
    emit("close");
};

const saveReservation = async () => {
    if (isReadonly.value) {
        return;
    }

    resetErrors();
    validate();

    if (Object.values(errors.value).some(Boolean)) {
        return;
    }

    const payload = {
        startAt: formatDateWithTimezone(startDate.value),
        endAt: formatDateWithTimezone(endDate.value),
        subject: subject.value?.trim(),
    };

    const request = isEditing.value
        ? MeetingRoomService.updateMeetingRoom(props.meetingRoom.id, payload)
        : MeetingRoomService.createMeetingRoom(payload);

    request.then((res) => {
        if (res && (res.status === 200 || res.status === 201)) {
            notification.addNotification({
                type: "success",
                message: isEditing.value ? "Reserva actualizada" : "Reserva creada",
                duration: 4000,
            });
            emit("finish");
            emit("close");
        }
    });
};

const resetErrors = () => {
    Object.keys(errors.value).forEach((key) => {
        errors.value[key] = false;
    });
};

const validate = () => {
    syncCombinedDates();

    if (!startDate.value) {
        errors.value.startDate = true;
    }

    if (!endDate.value) {
        errors.value.endDate = true;
    }

    if (!subject.value?.trim()) {
        errors.value.subject = true;
    }

    if (startDate.value && endDate.value) {
        const normalizedStart = roundDateToNearestHalfHour(new Date(startDate.value));
        const normalizedEnd = roundDateToNearestHalfHour(new Date(endDate.value));
        const now = new Date();

        if ((!isEditing.value && normalizedStart <= now) || normalizedEnd <= normalizedStart) {
            errors.value.startDate = true;
        }

        if (normalizedEnd <= normalizedStart || (isEditing.value && normalizedEnd <= now)) {
            errors.value.endDate = true;
        }

        if (isPublicHolidayDate(formatDatePart(normalizedStart))) {
            errors.value.startDate = true;
        }

        if (isPublicHolidayDate(formatDatePart(normalizedEnd))) {
            errors.value.endDate = true;
        }
    }
};

const disableStartDates = (date) => {
    if (isPublicHolidayDate(formatDatePart(date))) {
        return true;
    }

    if (date.getTime() < today.value.getTime()) {
        return true;
    }

    const nextSlot = getNextStrictHalfHour(new Date());
    const dateToCheck = new Date(date);
    const now = new Date();

    return (
        dateToCheck.getFullYear() === now.getFullYear() &&
        dateToCheck.getMonth() === now.getMonth() &&
        dateToCheck.getDate() === now.getDate() &&
        (
            nextSlot.getFullYear() !== now.getFullYear() ||
            nextSlot.getMonth() !== now.getMonth() ||
            nextSlot.getDate() !== now.getDate()
        )
    );
};

const disableEndDates = (date) => {
    if (isPublicHolidayDate(formatDatePart(date))) {
        return true;
    }

    if (date.getTime() < today.value.getTime()) {
        return true;
    }

    if (!startDay.value) {
        return false;
    }

    const current = new Date(date);
    const start = new Date(startDay.value);

    return isBeforeDateOnly(current, start);
};

const manageChangeStartDay = (value) => {
    if (!value || !startTime.value) {
        errors.value.startDate = true;
        return;
    }

    if (value === formatDatePart(new Date()) && startTime.value < startMinTime.value) {
        startTime.value = startMinTime.value;
    }

    startDate.value = combineDateAndTime(value, startTime.value);
    errors.value.startDate = false;
    ensureEndAfterStart();
};

const manageChangeStartTime = (value) => {
    if (!value || !startDay.value) {
        errors.value.startDate = true;
        return;
    }

    if (startDay.value === formatDatePart(new Date()) && value < startMinTime.value) {
        startTime.value = startMinTime.value;
    }

    startDate.value = combineDateAndTime(startDay.value, startTime.value);
    errors.value.startDate = false;
    ensureEndAfterStart();
};

const manageChangeEndDay = (value) => {
    if (!value || !endTime.value) {
        errors.value.endDate = true;
        return;
    }

    endDate.value = combineDateAndTime(value, endTime.value);
    errors.value.endDate = false;
    ensureEndAfterStart();
};

const manageChangeEndTime = (value) => {
    if (!value || !endDay.value) {
        errors.value.endDate = true;
        return;
    }

    endDate.value = combineDateAndTime(endDay.value, value);
    errors.value.endDate = false;
    ensureEndAfterStart();
};

const syncCombinedDates = () => {
    startDate.value = startDay.value && startTime.value
        ? combineDateAndTime(startDay.value, startTime.value)
        : null;
    endDate.value = endDay.value && endTime.value
        ? combineDateAndTime(endDay.value, endTime.value)
        : null;
};

const ensureEndAfterStart = () => {
    syncCombinedDates();

    if (!startDate.value) {
        return;
    }

    if (!endDate.value || new Date(endDate.value) <= new Date(startDate.value)) {
        const nextHalfHour = new Date(startDate.value);
        nextHalfHour.setMinutes(nextHalfHour.getMinutes() + 30);
        syncEndPartsFromDate(nextHalfHour);
    }

    errors.value.startDate = false;
    errors.value.endDate = false;
};

const isBeforeDateOnly = (dateA, dateB) => {
    const first = new Date(dateA.getFullYear(), dateA.getMonth(), dateA.getDate()).getTime();
    const second = new Date(dateB.getFullYear(), dateB.getMonth(), dateB.getDate()).getTime();
    return first < second;
};

const isPublicHolidayDate = (dateValue) => {
    return props.publicHolidayDates.includes(dateValue);
};

const roundDateToNearestHalfHour = (date) => {
    const normalizedDate = new Date(date);
    normalizedDate.setSeconds(0, 0);

    const minutes = normalizedDate.getMinutes();
    if (minutes === 0 || minutes === 30) {
        return normalizedDate;
    }

    if (minutes < 15) {
        normalizedDate.setMinutes(0);
        return normalizedDate;
    }

    if (minutes < 45) {
        normalizedDate.setMinutes(30);
        return normalizedDate;
    }

    normalizedDate.setHours(normalizedDate.getHours() + 1);
    normalizedDate.setMinutes(0);
    return normalizedDate;
};

const roundDateToNextHalfHour = (date) => {
    const normalizedDate = roundDateToNearestHalfHour(new Date(date));

    if (normalizedDate.getTime() < date.getTime()) {
        normalizedDate.setMinutes(normalizedDate.getMinutes() + 30);
    }

    return normalizedDate;
};

const getNextStrictHalfHour = (date) => {
    const normalizedDate = roundDateToNextHalfHour(new Date(date));

    if (normalizedDate.getTime() <= date.getTime()) {
        normalizedDate.setMinutes(normalizedDate.getMinutes() + 30);
    }

    return normalizedDate;
};

const syncStartPartsFromDate = (date) => {
    const normalizedDate = roundDateToNearestHalfHour(new Date(date));
    startDay.value = formatDatePart(normalizedDate);
    startTime.value = formatTimePart(normalizedDate);
    startDate.value = normalizedDate;
};

const syncEndPartsFromDate = (date) => {
    const normalizedDate = roundDateToNearestHalfHour(new Date(date));
    endDay.value = formatDatePart(normalizedDate);
    endTime.value = formatTimePart(normalizedDate);
    endDate.value = normalizedDate;
};

const formatDatePart = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatTimePart = (date) => {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
};

const combineDateAndTime = (dateValue, timeValue) => {
    const combinedDate = new Date(`${dateValue}T${timeValue}:00`);
    return roundDateToNearestHalfHour(combinedDate);
};

const addMinutesToTime = (timeValue, minutesToAdd) => {
    const [hours, minutes] = timeValue.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    date.setMinutes(date.getMinutes() + minutesToAdd);

    if (date.getDate() !== new Date().getDate()) {
        return null;
    }

    return formatTimePart(date);
};

const loadReservationData = () => {
    const startValue = props.meetingRoom?.startAt || props.meetingRoom?.startDate;
    const endValue = props.meetingRoom?.endAt || props.meetingRoom?.endDate;

    if (startValue) {
        syncStartPartsFromDate(new Date(startValue));
    }

    if (endValue) {
        syncEndPartsFromDate(new Date(endValue));
    }

    subject.value = props.meetingRoom?.subject || props.meetingRoom?.description || "";

    const newToday = new Date();
    newToday.setHours(0, 0, 0, 0);
    today.value = newToday;
};

const loadInitialReservationData = () => {
    const initialStart = parseInitialStartAt(props.initialStartAt);

    if (!initialStart) {
        const roundedStart = getNextStrictHalfHour(new Date());
        syncStartPartsFromDate(roundedStart);

        const defaultEnd = new Date(roundedStart);
        defaultEnd.setMinutes(defaultEnd.getMinutes() + 30);
        syncEndPartsFromDate(defaultEnd);
        return;
    }

    const initialStartDate = combineDateAndTime(initialStart.day, initialStart.time);
    const normalizedStartDate = initialStartDate <= new Date()
        ? getNextStrictHalfHour(new Date())
        : initialStartDate;

    syncStartPartsFromDate(normalizedStartDate);

    const defaultEnd = new Date(startDate.value);
    defaultEnd.setMinutes(defaultEnd.getMinutes() + 30);
    syncEndPartsFromDate(defaultEnd);
};

const parseInitialStartAt = (value) => {
    if (!value) {
        return null;
    }

    const exactLocalDateTime = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
    if (exactLocalDateTime) {
        return {
            day: exactLocalDateTime[1],
            time: exactLocalDateTime[2],
        };
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return {
        day: formatDatePart(date),
        time: formatTimePart(date),
    };
};

const formatDateWithTimezone = (dateValue) => {
    const date = new Date(dateValue);
    const offset = date.getTimezoneOffset();
    const sign = offset > 0 ? "-" : "+";
    const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0");
    const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, "0");

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMinutes}`;
};
</script>

<template>
    <div class="meeting-room-dialog">
        <div class="my-header">
            <div class="title-text dialog-title">
                <IconMeetingRoomReservation class="iconMeeting" />
                {{ dialogTitle }}
            </div>
            <img class="close-icon pointer justify-content-end" src="@/assets/images/aspa_blue_small_entrada.png" alt=""
                @click="closeDialog()">
        </div>
        <div class="row dialog-content">
            <div class="col-md-6 el-col-xs-24 mt-3">
                <label class="w-100" for="startDate">Inicio:</label>
                <div class="row time-picker-row mx-0">
                    <div class="col-7 px-0 pr-1">
                        <div class="form-item date-picker">
                            <el-date-picker v-model="startDay" id="startDate" class="w-100" type="date"
                                format="DD/MM/YYYY" value-format="YYYY-MM-DD" :clearable="false"
                                :prefix-icon="customPrefix" :disabled-date="disableStartDates"
                                :disabled="isReadonly" @change="manageChangeStartDay" />
                        </div>
                    </div>
                    <div class="col-5 px-0 pl-1" style="margin-left: 5px;">
                        <div class="form-item">
                            <el-time-select v-model="startTime" class="w-100" start="00:00" step="00:30" end="23:30"
                                :min-time="startMinTime" :clearable="false" :disabled="isReadonly"
                                @change="manageChangeStartTime" />
                        </div>
                    </div>
                </div>
                <small v-if="errors.startDate" class="invalid-feedback mb-0">
                    La fecha de inicio no es válida
                </small>
            </div>
            <div class="col-md-6 el-col-xs-24 mt-3">
                <label class="w-100" for="endDate">Fin:</label>
                <div class="row time-picker-row mx-0">
                    <div class="col-7 px-0 pr-1">
                        <div class="form-item date-picker">
                            <el-date-picker v-model="endDay" id="endDate" class="w-100" type="date" format="DD/MM/YYYY"
                                value-format="YYYY-MM-DD" :clearable="false" :prefix-icon="customPrefix"
                                :disabled-date="disableEndDates" :disabled="isReadonly" @change="manageChangeEndDay" />
                        </div>
                    </div>
                    <div class="col-5 px-0 pl-1" style="margin-left: 5px;">
                        <div class="form-item">
                            <el-time-select v-model="endTime" class="w-100" start="00:00" step="00:30" end="23:30"
                                :min-time="endMinTime" :clearable="false" :disabled="isReadonly"
                                @change="manageChangeEndTime" />
                        </div>
                    </div>
                </div>
                <small v-if="errors.endDate" class="invalid-feedback mb-0">
                    La fecha de fin no es válida
                </small>
            </div>
            <div class="col-md-12 el-col-xs-24 mt-3">
                <label class="w-100" for="subject">Asunto:</label>
                <textarea v-model="subject" id="subject" rows="4" class="w-100 p-2" :disabled="isReadonly"
                    placeholder="Indica el asunto de la reserva" @input="errors.subject = false"></textarea>
                <small v-if="errors.subject" class="invalid-feedback mb-0">
                    Tienes que completar el asunto
                </small>
            </div>
            <div class="row mt-4">
                <div class="col d-flex justify-content-end gap-3 dialog-actions">
                    <button class="btn btn_yellow px-4 dialog-action-button" @click="closeDialog()">
                        Cancelar
                    </button>
                    <button v-if="!isReadonly" class="btn btn_red px-4 dialog-action-button" @click="saveReservation()">
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.meeting-room-dialog {
    padding-bottom: 8px;
}

.my-header {
    margin-top: 2px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 12px;
}

.dialog-title {
    display: flex;
    align-items: center;
    gap: 12px;
}

.iconMeeting {
    width: 40px;
    fill: $bg-blue;
}

.dialog-content {
    margin: 0;
}

.dialog-actions {
    width: 100%;
}

.dialog-action-button {
    width: 40%;
}

.time-picker-row {
    row-gap: 8px;
    flex-wrap: nowrap;
}

textarea {
    border: 1px solid black;
    border-radius: 20px;
    resize: vertical;
    min-height: 120px;
}

@media (max-width: 767px) {
    .meeting-room-dialog {
        padding: 0 2px 8px;
    }

    .my-header {
        align-items: flex-start;
    }

    .dialog-title {
        font-size: 26px;
        line-height: 1.15;
    }

    .dialog-actions {
        flex-direction: column;
    }

    .dialog-action-button {
        width: 100%;
    }

    .time-picker-row {
        flex-wrap: nowrap;
    }
}
</style>
